/**
 * useOptionsSource Hook
 *
 * Dynamically loads options for config fields based on their options_source.
 * Supports:
 * - agents: List of active agents
 * - chatModels: Chat model options
 * - imageModels: Image generation models
 * - outputSchemas: Output schemas for a specific agent
 * - themes: Lucky Charm themes
 * - fonts: Lucky Charm fonts
 */

import { useMemo } from 'react';
import type { NodeData } from '@/types/imageWorkflow';
import { usePlaygroundAgents, useLuckyCharmThemes, useLuckyCharmFonts } from '@/features/playground/hooks/usePlayground';
import { useCatalogModels } from '@/lib/queries/useCatalogModels';
import { useOutputSchemasForAgent } from '@/features/system-settings/hooks/useOutputSchemas';

interface OptionsSourceResult {
  options: Array<{ value: string; label: string }>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to load dynamic options based on options_source
 *
 * @param optionsSource - The source type ('agents', 'chatModels', etc.)
 * @param nodeData - Current node data (used for context-dependent options like outputSchemas)
 */
export function useOptionsSource(
  optionsSource: string | undefined,
  nodeData?: NodeData
): OptionsSourceResult {
  // Fetch all possible sources (React hooks must be called unconditionally)
  const agentsQuery = usePlaygroundAgents();
  const chatModelsQuery = useCatalogModels('text');
  const imageModelsQuery = useCatalogModels('image');
  const themesQuery = useLuckyCharmThemes();
  const fontsQuery = useLuckyCharmFonts();

  // For outputSchemas, we need the agent_type from nodeData
  const agentType = nodeData?.agent_type as string | undefined;
  const outputSchemasQuery = useOutputSchemasForAgent(
    optionsSource === 'outputSchemas' ? agentType : undefined
  );

  // Transform and return the appropriate options
  return useMemo(() => {
    if (!optionsSource) {
      return { options: [], isLoading: false, error: null };
    }

    switch (optionsSource) {
      case 'agents':
        return {
          options: (agentsQuery.data || []).map((agent) => ({
            value: agent.agent_type,
            label: agent.name || agent.agent_type,
          })),
          isLoading: agentsQuery.isLoading,
          error: agentsQuery.error,
        };

      case 'chatModels':
        return {
          options: (chatModelsQuery.data || []).map((model) => ({
            value: model.id,
            label: model.id, // ModelInfo only has id, no display name
          })),
          isLoading: chatModelsQuery.isLoading,
          error: chatModelsQuery.error,
        };

      case 'imageModels':
        return {
          options: (imageModelsQuery.data || []).map((model) => ({
            value: model.id,
            label: model.display_name || model.id,
          })),
          isLoading: imageModelsQuery.isLoading,
          error: imageModelsQuery.error,
        };

      case 'outputSchemas':
        return {
          options: (outputSchemasQuery.data?.schemas || []).map((schema) => ({
            value: schema.key,
            label: schema.name || schema.key,
          })),
          isLoading: outputSchemasQuery.isLoading,
          error: outputSchemasQuery.error,
        };

      case 'themes':
        return {
          options: (themesQuery.data || []).map((theme) => ({
            value: String(theme.id),
            label: theme.display_name || String(theme.id),
          })),
          isLoading: themesQuery.isLoading,
          error: themesQuery.error,
        };

      case 'fonts':
        return {
          options: (fontsQuery.data?.fonts || []).map((font) => ({
            value: String(font.id),
            label: font.name,
          })),
          isLoading: fontsQuery.isLoading,
          error: fontsQuery.error,
        };

      default:
        console.warn(`[useOptionsSource] Unknown options_source: ${optionsSource}`);
        return { options: [], isLoading: false, error: null };
    }
  }, [
    optionsSource,
    agentsQuery.data,
    agentsQuery.isLoading,
    agentsQuery.error,
    chatModelsQuery.data,
    chatModelsQuery.isLoading,
    chatModelsQuery.error,
    imageModelsQuery.data,
    imageModelsQuery.isLoading,
    imageModelsQuery.error,
    outputSchemasQuery.data,
    outputSchemasQuery.isLoading,
    outputSchemasQuery.error,
    themesQuery.data,
    themesQuery.isLoading,
    themesQuery.error,
    fontsQuery.data,
    fontsQuery.isLoading,
    fontsQuery.error,
  ]);
}

export default useOptionsSource;
